import { flatten } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { DeleteConfirmation } from 'components/DeleteConfirmation';
import { useMessage } from 'components/ui';
import { type AtlasGqlHorizonDamage, useDeleteHorizonDamageMutation } from 'types/atlas-graphql';

interface DeleteDamageProps {
  damage: AtlasGqlHorizonDamage;
}

export const DeleteDamage: React.FunctionComponent<DeleteDamageProps> = ({ damage }) => {
  const { t } = useTranslation();
  const message = useMessage();
  const history = useHistory();
  const [deleteDamage] = useDeleteHorizonDamageMutation({
    variables: { input: { id: damage.id } },
    update: cache => {
      cache.evict({ id: cache.identify(damage) });
    },
    onCompleted: () => {
      message.success(t('damage.damageDeletedSuccess', 'Damage has been deleted'));
      history.push('/damages2');
    },
  });

  const hasMultipleObservations: boolean =
    (flatten(damage.observationGroups?.map(group => group?.observations ?? [])) ?? []).length > 1;
  const warningCopy: string = hasMultipleObservations
    ? t(
        'damage.deleteWarningMultiple',
        'This action will delete the Damage, the linked Observations,' +
          ' all Propagation Details, and any Comments that may have been made.' +
          ' This action is irreversible. If you wish to preserve any of the linked data,' +
          ' use the Large Image Viewer to remove any unwanted Observations.'
      )
    : t(
        'damage.deleteWarningSingle',
        'This action will delete the Damage, the linked Observation,' +
          ' and any Comments that have been made. This action is irreversible.'
      );

  return (
    <DeleteConfirmation
      id="delete-damage-button"
      type="button"
      deleteBtnType="default"
      title={t('damage.deleteDamage', 'Delete Damage')}
      warningContent={warningCopy}
      disabled={!!damage.tasks?.length}
      useToolTip
      toolTipMessage={t(
        'damage.deletionUnavailableTasks',
        'Damage deletion is not available due to associated Tasks.'
      )}
      errorInfo={t('damage.deleteErrorRetry', 'Error deleting damage. Please try again.')}
      onDelete={async () => {
        await deleteDamage();
      }}
    />
  );
};
